<template>
    <div
        v-if="isVisible"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/65"
        data-testid="match-over-modal"
    >
        <div class="bg-bg border-stroke-1 mx-4 w-full max-w-md border shadow-xl">
            <div class="p-4">
                <div class="mb-6 text-center text-sm">
                    <h2 class="font-title mb-2 text-2xl font-bold text-white uppercase">
                        {{ $t('match_over.title') }}
                    </h2>
                    <p>{{ $t('match_over.winner_intro') }}</p>
                </div>

                <div class="space-y-4">
                    <div
                        v-for="team in teams"
                        :key="team.id"
                        data-testid="match-over-team"
                        :data-winner="isTeamWinner(team.id)"
                        :class="[
                            'border p-4',
                            isTeamWinner(team.id)
                                ? 'border-green-500 bg-green-600'
                                : 'bg-bg border-stroke-1',
                        ]"
                    >
                        <div class="space-y-3">
                            <h3
                                v-if="team.players.length > 1"
                                class="mb-3 text-xl font-bold text-white uppercase"
                            >
                                {{ team.name }}
                            </h3>

                            <div class="flex flex-col gap-2">
                                <div
                                    v-for="player in team.players"
                                    :key="player.id"
                                    class="flex items-center justify-between"
                                >
                                    <div class="flex items-center space-x-2">
                                        <Avatar
                                            :size="12"
                                            :src="player.avatar"
                                            :alt="player.tag_name"
                                        />
                                        <span class="text-sm font-medium text-white">
                                            {{ player.tag_name }}
                                        </span>
                                    </div>
                                    <div v-if="isTeamWinner(team.id)" class="text-2xl">🏆</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div
                class="border-stroke-1 mt-4 grid gap-4 border-t p-4"
                :class="matchStore.isRemotePlay ? 'grid-cols-1' : 'grid-cols-2'"
            >
                <!-- Online matches are played across multiple devices, so
                     there's no single client that could set up and start a
                     rematch on everyone's behalf - game stats is the only
                     action offered. -->
                <DiagonalCutButton
                    v-if="!matchStore.isRemotePlay"
                    variant="secondary"
                    cut-position="right-top"
                    @click="rematch"
                >
                    {{ $t('match_over.rematch') }}
                </DiagonalCutButton>
                <DiagonalCutButton
                    variant="primary"
                    cut-position="left-top"
                    data-testid="match-over-game-stats"
                    @click="showGameStats"
                >
                    {{ $t('match_over.game_stats') }}
                </DiagonalCutButton>
            </div>
        </div>
    </div>
</template>

<script>
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import Avatar from '@/components/Avatar.vue'
import { useMatchStore } from '@/stores/match.js'

export default {
    name: 'MatchOverModal',
    components: { DiagonalCutButton, Avatar },
    props: {
        isVisible: {
            type: Boolean,
            default: false,
        },
        matchData: {
            type: Object,
            default: () => ({}),
        },
    },
    watch: {
        isVisible: {
            immediate: true,
            async handler(newValue) {
                if (newValue && this.matchStore.match.id) {
                    try {
                        await this.matchStore.endMatch(this.matchStore.match.id)
                    } catch (error) {
                        console.error('Failed to end match:', error)
                    }
                }
            },
        },
    },
    computed: {
        matchStore() {
            return useMatchStore()
        },

        matchProfiles() {
            return this.matchData?.profiles || []
        },

        winnerId() {
            return this.matchData?.winner_id
        },

        teams() {
            return this.matchStore.getTeams
        },

        winningTeam() {
            if (!this.winnerId) return null
            return this.teams.find((team) => team.id === this.winnerId)
        },
    },
    methods: {
        isTeamWinner(teamId) {
            return String(this.winnerId) === String(teamId)
        },

        rematch() {
            this.$emit('rematch')
        },

        showGameStats() {
            this.$emit('show-game-stats')
        },
    },
}
</script>
